import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  FullscreenSpinner,
  type Column,
} from '@shared/ui';
import { manufacturerApi, type ManufacturerListItem } from '@entities/manufacturer';
import type { KycStatus } from '@entities/user';
import { OnboardingWizard } from '@features/manufacturer-onboarding';
import { formatDateTime } from '@shared/lib/format';
import { ROUTES } from '@shared/config/routes';
export { ManufacturerDetailPage } from './ui/manufacturer-detail-page';

const kycVariant: Record<KycStatus, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  NONE: 'secondary',
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'destructive',
};

const COLUMNS: Column<ManufacturerListItem>[] = [
  {
    key: 'company',
    header: 'Company',
    cell: (r) => (
      <Link to={ROUTES.manufacturerDetail(r.id)} className="font-medium text-primary hover:underline">
        {r.companyName}
      </Link>
    ),
  },
  { key: 'country', header: 'Country', cell: (r) => r.country },
  {
    key: 'kyc',
    header: 'KYC',
    cell: (r) => <Badge variant={kycVariant[r.kycStatus]}>{r.kycStatus}</Badge>,
  },
  {
    key: 'eco',
    header: 'Eco',
    cell: (r) => (r.ecoProducer ? <Badge variant="success">eco</Badge> : '—'),
  },
  { key: 'active', header: 'Active', cell: (r) => (r.active ? '✓' : '—') },
  { key: 'updated', header: 'Updated', cell: (r) => formatDateTime(r.updatedAt) },
];

export function ManufacturersListPage() {
  const [searchParams] = useSearchParams();
  const kyc = searchParams.get('kyc') as KycStatus | null;
  const [wizardOpen, setWizardOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['manufacturers', { kyc }],
    queryFn: () =>
      manufacturerApi.list({
        kyc: kyc ? [kyc] : undefined,
        size: 50,
      }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          Производители{kyc ? ` · ${kyc}` : ''}
        </h1>
        <Button onClick={() => setWizardOpen(true)}>+ Добавить производителя</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <FullscreenSpinner />
          ) : (
            <DataTable
              rows={data?.content ?? []}
              columns={COLUMNS}
              rowKey={(r) => r.id}
              empty={<EmptyState title="Список пуст" />}
            />
          )}
        </CardContent>
      </Card>

      <OnboardingWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}
